import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import api from '../../services/api';

const ADMIN_EMAIL = 'brunoruotulo@gmail.com';

function sairOverlay() {
  const original = localStorage.getItem('token_original');
  localStorage.removeItem('token_original');
  if (original) {
    localStorage.setItem('token', original);
    window.location.href = '/dashboard';
  } else {
    // fallback: sem token original, faz logout
    localStorage.removeItem('token');
    window.location.href = '/login';
  }
}

function TrocaEmpresa() {
  const { user } = useAuth();
  const ref = useRef(null);
  const [aberto, setAberto] = useState(false);
  const [empresas, setEmpresas] = useState([]);

  useEffect(() => {
    if (user?.email !== ADMIN_EMAIL) return;
    api.get('/auth/tenants').then(r => setEmpresas(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!aberto) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aberto]);

  const trocar = async (tenantId) => {
    setAberto(false);
    try {
      // Sempre usa o token original de Bruno para a chamada de impersonação
      // (evita encadear overlay sobre overlay no backend)
      const tokenOriginal = localStorage.getItem('token_original') || localStorage.getItem('token');
      const { data } = await api.post(
        `/auth/impersonate/${tenantId}`,
        {},
        { headers: { Authorization: `Bearer ${tokenOriginal}` } }
      );
      localStorage.setItem('token_original', tokenOriginal);
      localStorage.setItem('token', data.token);
      window.location.href = '/dashboard';
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao trocar de empresa.');
    }
  };


  if (user?.email !== ADMIN_EMAIL) return null;

  const nomeAtual = user?.tenantName || '—';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setAberto(v => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-dark-700 hover:bg-dark-600 text-slate-300 hover:text-white transition-colors border border-dark-600"
        title="Trocar empresa"
      >
        <span>🏢</span>
        <span className="max-w-[120px] truncate">{nomeAtual}</span>
        <span className="text-slate-500">{aberto ? '▲' : '▼'}</span>
      </button>

      {aberto && (
        <div className="absolute left-0 top-9 w-52 bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-[var(--surface-4)]">
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Admin — trocar empresa</p>
          </div>
          <div className="py-1">
            {empresas.map(e => (
              <button
                key={e.id}
                onClick={() => trocar(e.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-[var(--surface-3)] transition-colors ${
                  e.name === nomeAtual ? 'text-blue-400 font-semibold' : 'text-slate-300'
                }`}
              >
                {e.name === nomeAtual && <span className="text-blue-400">✓</span>}
                {e.name !== nomeAtual && <span className="w-3" />}
                <span className="flex-1 truncate">{e.name}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${
                  e.planoCalibracao === 'full'
                    ? 'bg-blue-900/30 border-blue-800/40 text-blue-400'
                    : 'bg-slate-800 border-slate-700 text-slate-500'
                }`}>
                  {e.planoCalibracao === 'full' ? 'full' : 'ext'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const GROUP_STYLE = {
  vencido:                   { dot: 'bg-red-500',    label: 'text-red-400',    bg: 'bg-red-950/30',    border: 'border-red-800/40' },
  a_vencer:                  { dot: 'bg-amber-500',  label: 'text-amber-400',  bg: 'bg-amber-950/30',  border: 'border-amber-800/40' },
  bloqueado:                 { dot: 'bg-slate-500',  label: 'text-slate-400',  bg: 'bg-slate-800/30',  border: 'border-slate-700/40' },
  padraoReferencia_vencido:  { dot: 'bg-red-400',    label: 'text-red-300',    bg: 'bg-red-950/30',    border: 'border-red-800/40' },
  padraoReferencia_a_vencer: { dot: 'bg-orange-400', label: 'text-orange-300', bg: 'bg-orange-950/30', border: 'border-orange-800/40' },
};

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const bellRef = useRef(null);

  const [notifs, setNotifs] = useState({ total: 0, groups: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/notifications');
      setNotifs(res.data);
    } catch {
      // silencioso — não interrompe o fluxo
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 5 * 60 * 1000); // atualiza a cada 5 min
    return () => clearInterval(interval);
  }, [fetchNotifs]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const goToGroup = (type) => {
    setOpen(false);
    if (type.startsWith('padraoReferencia')) {
      navigate('/padroes-referencia');
    } else {
      navigate(`/instruments?status=${type}`);
    }
  };

  const formatDays = (days) => {
    if (days === null) return '—';
    if (days < 0) return `${Math.abs(days)}d atraso`;
    if (days === 0) return 'hoje';
    return `${days}d`;
  };

  const dayClass = (days) => {
    if (days === null) return 'text-slate-500';
    if (days < 0) return 'text-red-400 font-semibold';
    if (days <= 7) return 'text-red-400 font-semibold';
    if (days <= 30) return 'text-amber-400 font-semibold';
    return 'text-yellow-400';
  };

  return (
    <header className="h-12 bg-dark-800 border-b border-dark-600 flex items-center px-4 flex-shrink-0">
      {/* Hamburger — mobile only */}
      <button
        onClick={onMenuClick}
        className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-dark-700 hover:text-white transition-colors mr-2 flex-shrink-0"
        aria-label="Menu"
      >
        <span className="text-base leading-none">☰</span>
      </button>

      <div className="text-sm font-semibold text-accent-blue tracking-tight">
        Calibra<span className="text-white">AI</span>
      </div>
      <div className="hidden sm:block w-px h-5 bg-dark-600 mx-3" />
      <div className="hidden sm:block text-xs" style={{ color: 'var(--text-muted)' }}>{tenant?.nomeEmpresa || tenant?.name || ''}</div>
      <div className="flex-1" />

      <div className="flex items-center gap-3">

        {/* ── Banner modo admin overlay ─────────────────────────────────── */}
        {user?.isAdminOverlay && (
          <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-red-950/60 border border-red-700/60 text-red-300 text-xs font-semibold">
            <span>⚠️ MODO ADMIN</span>
            <span className="text-red-500">·</span>
            <span className="font-normal text-red-400">{user.tenantName}</span>
            <button
              onClick={sairOverlay}
              className="ml-1 px-2 py-0.5 rounded bg-red-800/60 hover:bg-red-700/60 text-red-200 transition-colors"
            >
              Sair
            </button>
          </div>
        )}

        {/* ── Seletor de empresa (admin) ────────────────────────────────── */}
        <div className="hidden lg:block">
          <TrocaEmpresa />
        </div>

        {/* ── Sino de notificações ──────────────────────────────────────── */}
        <div className="relative" ref={bellRef}>
          <button
            onClick={() => { setOpen(v => !v); if (!open) fetchNotifs(); }}
            className="relative w-7 h-7 flex items-center justify-center rounded-lg hover:bg-dark-700 transition-colors"
            title="Notificações"
          >
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>🔔</span>
            {notifs.total > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-600 rounded-full text-[9px] flex items-center justify-center font-bold text-white px-0.5">
                {notifs.total > 99 ? '99+' : notifs.total}
              </span>
            )}
          </button>

          {/* Dropdown */}
          {open && (
            <div className="absolute right-0 top-9 w-80 bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl shadow-2xl z-50 overflow-hidden">

              {/* Cabeçalho do dropdown */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--surface-4)]">
                <span className="text-xs font-semibold text-white">Notificações</span>
                {loading ? (
                  <span className="text-[10px] text-slate-500">Atualizando...</span>
                ) : (
                  <button onClick={fetchNotifs} className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors">
                    ↻ Atualizar
                  </button>
                )}
              </div>

              {/* Conteúdo */}
              <div className="max-h-[400px] overflow-y-auto">
                {notifs.total === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                    <span className="text-2xl mb-2">✅</span>
                    <p className="text-xs">Sem alertas no momento</p>
                  </div>
                ) : (
                  notifs.groups.map((group) => {
                    const style = GROUP_STYLE[group.type] || GROUP_STYLE.bloqueado;
                    return (
                      <div key={group.type}>
                        {/* Cabeçalho do grupo */}
                        <button
                          onClick={() => goToGroup(group.type)}
                          className="w-full flex items-center gap-2 px-4 py-2 hover:bg-[var(--surface-3)] transition-colors group"
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${style.dot}`} />
                          <span className={`text-xs font-semibold flex-1 text-left ${style.label}`}>
                            {group.label}
                          </span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${style.bg} ${style.border} ${style.label}`}>
                            {group.items.length}
                          </span>
                          <span className="text-slate-600 group-hover:text-slate-400 text-[10px]">→</span>
                        </button>

                        {/* Itens do grupo */}
                        <div className="pb-1">
                          {group.items.slice(0, 5).map((item) => (
                            <button
                              key={item.id}
                              onClick={() => goToGroup(group.type)}
                              className="w-full flex items-center gap-3 px-4 py-1.5 hover:bg-[var(--surface-3)] transition-colors"
                            >
                              <span className="font-mono text-xs text-slate-300 w-20 text-left flex-shrink-0">{item.tag}</span>
                              <span className="text-[11px] text-slate-500 flex-1 text-left truncate">{item.description}</span>
                              <span className={`text-[10px] flex-shrink-0 ${dayClass(item.daysRemaining)}`}>
                                {formatDays(item.daysRemaining)}
                              </span>
                            </button>
                          ))}
                          {group.items.length > 5 && (
                            <button
                              onClick={() => goToGroup(group.type)}
                              className="w-full text-[10px] text-slate-600 hover:text-slate-400 py-1 text-center transition-colors"
                            >
                              + {group.items.length - 5} mais
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Rodapé */}
              {notifs.total > 0 && (
                <div className="border-t border-[var(--surface-4)] px-4 py-2">
                  <button
                    onClick={() => goToGroup('vencido')}
                    className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Ver todos os instrumentos →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Toggle de tema — desktop only */}
        <button
          onClick={toggle}
          title={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          className="hidden sm:flex w-7 h-7 rounded-lg items-center justify-center transition-colors hover:bg-dark-700 text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>

        {/* Usuário */}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-accent-blue-dark flex items-center justify-center text-xs font-bold text-white">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <button onClick={logout} className="text-xs hover:text-white transition-colors" style={{ color: 'var(--text-muted)' }}>
            Sair
          </button>
        </div>
      </div>
    </header>
  );
}
